import Icon from "@mdi/react";
import React, { FC } from "react";
import { Theme, useTheme } from "@material-ui/core/styles";
import { mdiCloudUpload } from "@mdi/js";
import { styled } from "@material-ui/styles";
import { useDropzone } from "react-dropzone";

type DropZoneProps = {
  explanatoryText: string;
  sampleLink: string;
  onDrop: (acceptedFiles: any) => void;
  didErrorOccurInUpload: boolean;
};

const Root = styled("div")({
  marginBottom: 16,
  width: "100%",
});

const StyledDropzone = styled("section")(({ theme }: { theme: Theme }) => ({
  width: "100%",
  flex: 1,
  minHeight: 160,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "24px 16px",
  border: `2px dashed ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  backgroundColor: theme.palette.brand.gray50,
  color: theme.palette.brand.gray900,
  outline: "none",
  transition: "border-color 0.15s ease-in-out",
  cursor: "pointer",
  textAlign: "center",
  '&[data-active="true"]': {
    borderColor: theme.palette.brand.coral,
  },
}));

const PrimaryText = styled("p")(({ theme }: { theme: Theme }) => ({
  margin: 0,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  color: theme.palette.brand.gray700,
}));

const SelectHighlight = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.blueAccent,
  fontWeight: 600,
}));

const HelperText = styled("p")(({ theme }: { theme: Theme }) => ({
  margin: 0,
  fontSize: 13,
  lineHeight: "18px",
  color: theme.palette.brand.gray500,
  whiteSpace: "nowrap",
}));

const ErrorText = styled("p")(({ theme }: { theme: Theme }) => ({
  margin: 0,
  fontSize: 13,
  lineHeight: "18px",
  color: theme.palette.brand.errorRed,
}));

const SampleLinkRow = styled("div")({
  display: "flex",
  justifyContent: "center",
  marginTop: 12,
});

const SampleLinkButton = styled("button")(({ theme }: { theme: Theme }) => ({
  background: "transparent",
  border: "none",
  padding: 0,
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.brand.blueAccent,
  cursor: "pointer",
  fontFamily: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: 2,
  "&:hover": {
    color: theme.palette.brand.coral,
  },
}));

const DropZone: FC<DropZoneProps> = ({ explanatoryText, sampleLink, onDrop, didErrorOccurInUpload }) => {
  const theme = useTheme();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: false,
  });

  return (
    <Root>
      <div {...getRootProps()}>
        <input {...getInputProps()} />
        <StyledDropzone data-active={isDragActive ? "true" : undefined}>
          <Icon path={mdiCloudUpload} title="Upload file" size={1.4} color={theme.palette.brand.blueAccent} />
          <PrimaryText>
            Drag and drop or <SelectHighlight>select from your computer</SelectHighlight>
          </PrimaryText>
          {didErrorOccurInUpload ? (
            <ErrorText>There was an error uploading your file. Please try again.</ErrorText>
          ) : (
            <HelperText>{explanatoryText}</HelperText>
          )}
        </StyledDropzone>
      </div>
      <SampleLinkRow>
        <SampleLinkButton
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            window.open(sampleLink, "_blank");
          }}
        >
          Sample CSV file
        </SampleLinkButton>
      </SampleLinkRow>
    </Root>
  );
};

export default DropZone;
